/* eslint-disable react-hooks/exhaustive-deps */
import { useFormikContext } from 'formik';
import { FormValues, BeneficialPersonalOwner, BeneficialBusinessOwner } from '../../schema';
import { useEffect, useRef, useMemo } from 'react';
import { BUSINESS_OWNERS_FORM_STRING, INDIVIDUAL_OWNERS_FORM_STRING } from './OwnersTree';

export const useMapNumbering = () => {
    const { values, setFieldValue } = useFormikContext<FormValues>();

    // Use refs to track previous values and avoid infinite loops
    const prevBusinessOwnersRef = useRef<string>('');
    const prevIndividualOwnersRef = useRef<string>('');

    // Function to normalize numbers ensuring sequential unique values
    const normalizeNumbers = <T extends BeneficialPersonalOwner | BeneficialBusinessOwner>(
        owners: T[]
    ): T[] => {
        return owners.map((owner, index) => ({
            ...owner,
            number: index + 1,
        }));
    };

    // Use useMemo to optimize performance for large arrays
    const businessOwnersKey = useMemo(() => {
        const owners = values.ownerInformation.businessBeneficialOwners || [];
        return JSON.stringify(
            owners.map((owner) => ({
                guid: owner.guid,
            }))
        );
    }, [values.ownerInformation.businessBeneficialOwners]);

    const individualOwnersKey = useMemo(() => {
        const owners = values.ownerInformation.individualBeneficialOwners || [];
        return JSON.stringify(
            owners.map((owner) => ({
                guid: owner.guid,
                // Exclude number from key to avoid circular dependency
            }))
        );
    }, [values.ownerInformation.individualBeneficialOwners]);

    // Only update if the data has actually changed
    useEffect(() => {
        if (businessOwnersKey !== prevBusinessOwnersRef.current) {
            const normalizedBusinessOwners = normalizeNumbers(
                values.ownerInformation.businessBeneficialOwners || []
            );
            setFieldValue(BUSINESS_OWNERS_FORM_STRING, normalizedBusinessOwners);
            prevBusinessOwnersRef.current = businessOwnersKey;
        }
    }, [businessOwnersKey, setFieldValue]);

    useEffect(() => {
        if (individualOwnersKey !== prevIndividualOwnersRef.current) {
            const normalizedIndividualOwners = normalizeNumbers(
                values.ownerInformation.individualBeneficialOwners || []
            );
            setFieldValue(INDIVIDUAL_OWNERS_FORM_STRING, normalizedIndividualOwners);
            prevIndividualOwnersRef.current = individualOwnersKey;
        }
    }, [individualOwnersKey, setFieldValue]);
};
